import { useBookingRecord } from "@/app/Bookings/Hooks/useBookingRecord";
import { bookingMessages } from "@/app/Bookings/Messages/bookingMessages";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { Avatar } from "@/components/catalyst/avatar";
import { getInitials } from "@/lib/Utils/getInitials";
import { ShieldCheckIcon } from "@heroicons/react/20/solid";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { BookingEditCustomerDetailsDialog } from "@/app/Bookings/Components/Dialogs/BookingEditCustomerDetailsDialog";
import { BookingBlockCustomerDialog } from "@/app/Bookings/Components/Dialogs/BookingBlockCustomerDialog";
import { Pencil, ShieldBan, X } from "lucide-react";
import dayjs from "dayjs";
import { CopyToClipboard } from "@/lib/Components/Common/CopyToClipboard";
import { Button } from "@/components/catalyst/button";
import { Badge } from "@/components/catalyst/badge";
import { useUnblockCustomer } from "@/app/Bookings/Hooks/useUnblockCustomer";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export function BookingCustomerCard() {
  const booking = useBookingRecord();
  const { open } = useDialog(BookingEditCustomerDetailsDialog);
  const { open: openBlockDialog } = useDialog(BookingBlockCustomerDialog);
  const { unblockCustomer, isUnblocking } = useUnblockCustomer();

  return (
    <Card className="">
      <CardHeader className="relative flex h-10 translate-y-2 justify-center">
        <Avatar
          className="mx-auto size-24 border border-gray-300 bg-white shadow-md"
          media={booking.user?.profilePicture}
          initials={getInitials(booking.name ?? "")}
        />
        <div className="absolute right-3 top-0">
          <Button
            outline
            onClick={() => {
              open({
                bookingId: booking.id,
              });
            }}
          >
            <Pencil data-slot="icon" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-6 text-sm">
        <div className="grid gap-3">
          <div className="flex items-center justify-between">
            <div className="font-semibold">Customer Information</div>
            {booking.userRating?.isBlocked ? (
              <div className="group relative">
                <Badge color="red" className="cursor-pointer">
                  <ShieldBan className="size-3" />
                  Blocked
                </Badge>
                <button
                  onClick={() => unblockCustomer(booking.id)}
                  disabled={isUnblocking}
                  className="absolute -right-2 -top-2 rounded-full bg-red-600 p-0.5 text-white opacity-0 transition-opacity hover:bg-red-700 disabled:opacity-50 group-hover:opacity-100"
                  title="Unblock customer"
                >
                  <X className="size-3" />
                </button>
              </div>
            ) : null}
          </div>
          <dl className="grid gap-3">
            <div className="flex items-center justify-between">
              <dt className="text-gray-500">Customer</dt>
              <dd className="flex items-center gap-1.5">
                <CopyToClipboard text={booking.name} />
                {booking.userRating?.isVerified ? (
                  <ShieldCheckIcon
                    className="size-4 text-green-600"
                    title="Verified customer"
                  />
                ) : null}
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-gray-500">Email</dt>
              <dd className="flex items-center gap-1.5">
                <CopyToClipboard text={booking.email} />
                {booking.userRating?.isVerifiedEmail ? (
                  <ShieldCheckIcon
                    className="size-4 text-green-600"
                    title="Verified email"
                  />
                ) : null}
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-gray-500">Phone</dt>
              <dd className="flex items-center gap-1.5">
                <CopyToClipboard text={booking.phone} />
                {booking.userRating?.isVerifiedPhone ? (
                  <ShieldCheckIcon
                    className="size-4 text-green-600"
                    title="Verified phone"
                  />
                ) : null}
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-gray-500">Date of birth</dt>
              <dd>
                <CopyToClipboard text={booking.date_of_birth}>
                  {dayjs(booking.date_of_birth).format("LL")}
                </CopyToClipboard>
              </dd>
            </div>
            {booking.address ? (
              <div className="flex items-center justify-between">
                <dt className="text-gray-500">Address</dt>
                <dd>
                  <CopyToClipboard text={booking.address} />
                </dd>
              </div>
            ) : null}
            {booking.gender ? (
              <div className="flex items-center justify-between">
                <dt className="text-gray-500">Gender</dt>
                <dd>{bookingMessages.gender[booking.gender]}</dd>
              </div>
            ) : null}
            <div className="flex items-center justify-between">
              <dt className="text-gray-500">Number of travellers</dt>
              <dd>{booking.number_of_travellers}</dd>
            </div>
          </dl>
        </div>
      </CardContent>
      <CardFooter className="bg-muted/50 flex flex-row items-center justify-between border-t px-6 py-1">
        <div className="flex items-center gap-2">
          {booking.userRating?.completedRelocationCount ? (
            <div className="text-xs text-gray-500">
              {booking.userRating.completedRelocationCount} completed relocation
              {booking.userRating.completedRelocationCount !== 1 ? "s" : ""}
            </div>
          ) : null}
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="flex">
              <button
                onClick={() =>
                  openBlockDialog({
                    bookingId: booking.id,
                  })
                }
                disabled={booking.userRating?.isBlocked}
                className="text-gray-400 transition-colors hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ShieldBan className="size-4" />
              </button>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {booking.userRating?.isBlocked
              ? "Customer is blocked"
              : "Block this customer"}
          </TooltipContent>
        </Tooltip>
      </CardFooter>
    </Card>
  );
}
